import { forEach, has } from 'lodash';
import {
  map,
  flow,
  getOr,
  flatMap,
  toPairs,
  sortBy,
  compose,
  startsWith,
  filter,
  head,
  values,
  join,
  fromPairs,
  omit,
} from 'lodash/fp';

import { isTableType } from 'constants/chart-roles';

import { attemptCast } from 'utils/bson';
import { generatePostProcessedSelectors } from 'utils/process-spec-and-data/post-agg-sorting';
import { getMultiSeriesParentChannel } from 'utils/chart/multi-series-name';

/**
 * Groups documents via the 'groups' fields, and attempts to make a new field for every dynamic column.
 * Handles cases where a group field a bad value, as well as multiple group fields.
 *
 * Multiple groups are done by building a key via concatenating the field name & the value together
 *
 * Runs linearly over the array
 * @param {Array} documents - An array of documents from the aggregation pipeline
 * @returns {Object} - Object containing the subHeaders (ie binned values etc) & the grouped documents
 */
function groupDocuments(documents) {
  const subHeaders = {};
  const groupedDocs = {};

  forEach(documents, doc => {
    const { dynamicColumns } = doc;

    // if it's a post processed value, we use the display value as the column name
    // otherwise we just use the value of the dynamic column
    // we use has instead of get since the value of the display might be undefined
    const dynColumn = has(dynamicColumns, 'display')
      ? dynamicColumns.display
      : dynamicColumns;
    subHeaders[dynColumn] = dynamicColumns;

    // need to use the display value in postProcessed fields to prevent grouping on [Object object]
    // otherwise the group should have a reasonable toString, so we use it as-is

    const groupKey = flow(
      toPairs,
      filter(
        compose(
          startsWith('group'),
          head
        )
      ),
      flatMap(([groupName, value]) => [
        groupName,
        getOr(`${value}`, 'display', value),
      ]),
      join('')
    )(doc);

    const groups = flow(
      toPairs,
      filter(
        compose(
          startsWith('group'),
          head
        )
      ),
      fromPairs
    )(doc);

    if (groupedDocs[groupKey]) {
      groupedDocs[groupKey][dynColumn] = attemptCast(doc.value);
    } else {
      groupedDocs[groupKey] = {
        ...groups,
        [dynColumn]: attemptCast(doc.value),
      };
    }
  });

  return {
    subHeaders,
    groupedDocs,
  };
}

/**
 * Generates a completed spec based off the already existing spec, the subheaders and the channels.
 *
 * Subheaders are the headers/columns that are meant to go under the dynamicColumns channel group.
 * @param {Object} subHeaders An object keyed by the display name, and with the values being the underlying table value (ie plain or {raw: ..., display: ...})
 * @param {Object} channels - Channels object
 * @param {Object} spec - The spec to update with dynamic columns
 * @returns {Object} - Spec object with the dynamic columns added to to
 */
function generateDynamicColumnSpec(subHeaders, channels, spec) {
  // functions to retrieve the parts of an object we need to sort over
  const headerSelectors = generatePostProcessedSelectors(
    channels.dynamicColumns
  ).map(selector => ([, raw]) => selector(raw));

  const dynamicColumns = flow(
    toPairs,
    sortBy(headerSelectors),
    map(([dynamicColumnName]) => ({
      headerName: `${dynamicColumnName}`,
      field: `${dynamicColumnName}`,
    }))
  )(subHeaders);

  const groupsSpec =
    filter(
      ch => getMultiSeriesParentChannel(ch.field) === 'group',
      spec.columnDefs
    ) || [];

  const dynamicColumnSpec = flow(
    filter(ch => getMultiSeriesParentChannel(ch.field) === 'dynamicColumns'),
    head,
    colSpec => colSpec || {},
    omit('field')
  )(spec.columnDefs);

  const valueSpec = flow(
    filter(ch => getMultiSeriesParentChannel(ch.field) === 'value'),
    head
  )(spec.columnDefs);

  return {
    ...spec,
    columnDefs: [
      ...groupsSpec,
      {
        ...dynamicColumnSpec,
        groupId: 'dynamicColumns',
        headerName: valueSpec
          ? valueSpec.headerName
          : dynamicColumnSpec.headerName,
        headerTooltip: valueSpec
          ? valueSpec.headerName
          : dynamicColumnSpec.headerName,
        children: dynamicColumns.map(child => ({
          ...valueSpec,
          ...child,
          suppressMovable: true,
          headerClass: dynamicColumnSpec.headerClass,
          headerTooltip: child.headerName,
        })),
      },
    ],
    gridOptions: {
      ...spec.gridOptions,
      suppressFieldDotNotation: true, // Dynamic columns can generate fields with dots ie decimals
    },
  };
}

/**
 * Generates dynamic columns: Columns that are data driven.
 *
 * This stage produces two outputs, the updated spec (with the new columns), and the updated documents to match.
 *
 * @param {Object} stageData - Object containing documents & an environment object with channels and chartType
 * @returns {Object} - Returns a new stage with the table documents grouped & the dynamic columns added
 */
function tableDynamicColumns(stageData) {
  const {
    environment: { chartType, channels },
    documents,
    spec,
  } = stageData;
  // skip the dynamic column binning if not applicable
  if (!isTableType(chartType) || !channels.dynamicColumns) {
    return {};
  }

  const { subHeaders, groupedDocs } = groupDocuments(documents);

  const dynamicColumnSpec = generateDynamicColumnSpec(
    subHeaders,
    channels,
    spec
  );

  return {
    documents: values(groupedDocs),
    spec: dynamicColumnSpec,
  };
}

export default {
  input: ['environment.chartType', 'environment.channels', 'documents', 'spec'],
  fn: tableDynamicColumns,
};
export { tableDynamicColumns, groupDocuments, generateDynamicColumnSpec };
